import type { GeoScoreSummary, SentimentHeatmapSummary, VisibilityScore } from '@lensora/shared'
import { Card, CardContent } from '@lensora/ui'
import { useQuery } from '@tanstack/react-query'
import { BarChart2, PlayCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  GeoLeaderboardHero,
  type LeaderboardEntry,
} from '@/components/dashboard/GeoLeaderboardHero'
import { PlatformCarousel } from '@/components/dashboard/PlatformCarousel'
import { PlatformRingsWidget } from '@/components/dashboard/PlatformRingsWidget'
import { SentimentHeatmap } from '@/components/dashboard/SentimentHeatmap'
import { TopCitedSources, type CitedDomain } from '@/components/dashboard/TopCitedSources'
import { ScoreHistoryChart } from '@/components/score/ScoreHistoryChart'
import { SubScoreRingsWidget, type SubScore } from '@/components/score/SubScoreRingsWidget'
import { useActiveWebsite } from '@/hooks/use-active-website'
import { useGrowthPlus } from '@/hooks/use-growth-plus'
import { useVisibilityOverview } from '@/hooks/use-visibility-score'
import { api } from '@/lib/api'
import { isSourceType } from '@/lib/source-types'

// ── Constants ─────────────────────────────────────────────────────────────────

// Weights mirror the API score engine (apps/api/src/lib/ai/scoring.ts).
// Colours match light-theme tokens — SVG/inline-style props can't read CSS vars.
const SUB_SCORE_META = [
  {
    id: 'presence',
    label: 'Presence',
    field: 'presenceScore',
    weight: 30,
    color: '#6366f1',
    description: 'How often your brand is mentioned across AI responses',
  },
  {
    id: 'sentiment',
    label: 'Sentiment',
    field: 'sentimentScore',
    weight: 20,
    color: '#16a34a',
    description: 'Positive vs neutral vs negative tone of mentions',
  },
  {
    id: 'authority',
    label: 'Authority',
    field: 'authorityScore',
    weight: 20,
    color: '#8b5cf6',
    description: 'How prominently your brand ranks within AI responses',
  },
  {
    id: 'coverage',
    label: 'Coverage',
    field: 'coverageScore',
    weight: 15,
    color: '#ca8a04',
    description: 'Breadth of query types in which your brand appears',
  },
  {
    id: 'competitor_gap',
    label: 'Competitor Gap',
    field: 'competitorGapScore',
    weight: 15,
    color: '#2563eb',
    description: 'Your presence relative to average competitor presence',
  },
] as const

function buildSubScores(latest: VisibilityScore): SubScore[] {
  return SUB_SCORE_META.map((m) => ({ ...m, score: Math.round(latest[m.field]) }))
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-20 animate-pulse rounded-lg border border-border bg-surface" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
        <div className="h-80 animate-pulse rounded-lg border border-border bg-surface" />
        <div className="grid grid-cols-2 gap-4 xl:col-span-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-lg border border-border bg-surface"
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <BarChart2 className="mb-4 h-12 w-12 text-text-muted opacity-40" />
        <p className="font-semibold text-text">No score data yet</p>
        <p className="mt-1 max-w-sm text-sm text-text-muted">
          Run your first simulation to generate your AI visibility score breakdown across Presence,
          Sentiment, Authority, Coverage, and Competitor Gap.
        </p>
        <Link
          to="/dashboard/prompts"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-accent px-5 py-2.5 text-sm font-semibold text-text-inverse hover:opacity-90"
        >
          <PlayCircle className="h-4 w-4" />
          Run first simulation
        </Link>
      </CardContent>
    </Card>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ScorePage() {
  const { websiteId } = useActiveWebsite()
  const { data, isLoading } = useVisibilityOverview(websiteId)
  const isGrowthPlus = useGrowthPlus()

  const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery({
    queryKey: ['leaderboard', websiteId],
    queryFn: () =>
      api
        .get<{
          leaderboard: LeaderboardEntry[]
        }>(`/api/competitors/leaderboard?website_id=${websiteId}`)
        .then((d) => d.leaderboard),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: geoData } = useQuery({
    queryKey: ['geo-score', websiteId],
    queryFn: () => api.get<GeoScoreSummary>(`/api/dashboard/geo-score?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: sentimentData, isLoading: sentimentLoading } = useQuery({
    queryKey: ['sentiment-heatmap', websiteId],
    queryFn: () =>
      api.get<SentimentHeatmapSummary>(`/api/dashboard/sentiment-heatmap?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: sourcesData, isLoading: sourcesLoading } = useQuery({
    queryKey: ['sources', websiteId],
    queryFn: () =>
      api
        .get<{ domains?: CitedDomain[] }>(`/api/sources?website_id=${websiteId}`)
        .then((d) => (d.domains ?? []).filter((domain) => isSourceType(domain.type))),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  if (isLoading) return <LoadingSkeleton />

  // A zero-query snapshot (manual recalc with no results) renders as empty state
  const latest = data?.latest && data.latest.queriesAnalyzed > 0 ? data.latest : null
  const subScores = latest ? buildSubScores(latest) : []
  const platformStats = geoData?.platformStats ?? {}

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text">Score Breakdown</h2>
        <p className="mt-1 text-sm text-text-muted">
          {latest
            ? `Computed from ${latest.queriesAnalyzed} simulation${latest.queriesAnalyzed !== 1 ? 's' : ''}`
            : 'Detailed analysis of your AI visibility score across all dimensions'}
        </p>
      </div>

      {!latest && <EmptyState />}

      {latest && (
        <>
          <PlatformCarousel platformStats={platformStats} isGrowthPlus={isGrowthPlus} />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <PlatformRingsWidget platformStats={platformStats} isGrowthPlus={isGrowthPlus} />
            <SentimentHeatmap data={sentimentData ?? null} isLoading={sentimentLoading} />
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <GeoLeaderboardHero
              score={Math.round(latest.overallScore)}
              geoData={geoData ?? null}
              leaderboard={leaderboardData ?? []}
              leaderboardLoading={leaderboardLoading}
            />

            <TopCitedSources domains={sourcesData ?? []} isLoading={sourcesLoading} />
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <SubScoreRingsWidget
              subScores={subScores}
              overallScore={Math.round(latest.overallScore)}
            />
            <ScoreHistoryChart history={data?.history ?? []} />
          </div>
        </>
      )}
    </div>
  )
}
